import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import './index.css';

function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [roleUpdates, setRoleUpdates] = useState({}); // Track pending role changes

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = window.localStorage.getItem('token');
        
        // Fetch projects
        const projectsRes = await fetch('/api/projects');
        if (!projectsRes.ok) throw new Error(`Failed to fetch projects: ${projectsRes.status}`);
        const projectsData = await projectsRes.json();
        setProjects(projectsData || []);
        
        // Fetch posts
        const postsRes = await fetch('/api/posts');
        if (!postsRes.ok) throw new Error(`Failed to fetch posts: ${postsRes.status}`);
        const postsData = await postsRes.json();
        setPosts(postsData || []);
        
        // Fetch users (admin only)
        const usersRes = await fetch('/api/admin/users', {
          headers: { 'Authorization': token }
        });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData || []);
        }
      } catch (err) {
        setError(err.message || 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleRoleChange = (userId, newRole) => {
    setRoleUpdates(prev => ({ ...prev, [userId]: newRole }));
  };

  const updateUserRole = async (userId) => {
    try {
      const token = window.localStorage.getItem('token');
      const newRole = roleUpdates[userId];
      
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!res.ok) throw new Error('Failed to update role');

      const data = await res.json();
      
      // Update local state
      setUsers(users.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      setRoleUpdates(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      
      setSuccessMessage(`${data.user.username}'s role updated to '${newRole}'`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-header">
        <h1>Admin Dashboard</h1>
        <p className="subtitle">Manage your venture studio platform</p>
      </div>

      {isLoading && <div className="loading-state">Loading dashboard…</div>}
      {error && <div className="error-state">Error: {error}</div>}

      {!isLoading && !error && (
        <>
          {successMessage && <div className="success-message">{successMessage}</div>}

          {/* User Management Section */}
          {users.length > 0 && (
            <div className="dashboard-section">
              <div className="section-header">
                <h2>User Management</h2>
                <span style={{ color: 'var(--primary-dark)', fontSize: '14px' }}>
                  {users.length} total users
                </span>
              </div>
              <div className="user-grid">
                {users.map(user => (
                  <div key={user.user_id} className="dashboard-card">
                    <div className="card-content">
                      <div>
                        <div className="card-title">
                          {user.firstName} {user.lastName}
                          <span className={`user-role-badge role-${user.role}`} style={{ marginLeft: '12px' }}>
                            {user.role}
                          </span>
                        </div>
                        <div className="card-meta">
                          @{user.username} • {user.email}
                        </div>
                      </div>
                      <div className="card-actions">
                        <select 
                          className="role-selector"
                          value={roleUpdates[user.user_id] || user.role}
                          onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                        >
                          <option value="user">User</option>
                          <option value="investor">Investor</option>
                          <option value="admin">Admin</option>
                        </select>
                        {roleUpdates[user.user_id] && roleUpdates[user.user_id] !== user.role && (
                          <button 
                            className="update-role-button"
                            onClick={() => updateUserRole(user.user_id)}
                          >
                            Update Role
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Projects</h2>
              <Link to="/admin/projects/new" className="action-button">+ Create Project</Link>
            </div>
            {projects.length === 0 ? (
              <div className="empty-state">No projects yet. Create your first project!</div>
            ) : (
              <div className="grid gap-12">
                {projects.map(project => (
                  <div key={project.project_id} className="dashboard-card">
                    <div className="card-content">
                      <div>
                        <div className="card-title">{project.title}</div>
                        <div className="card-meta">Status: {project.status || 'N/A'}</div>
                      </div>
                      <div className="card-actions">
                        <Link className="card-link" to={`/projects/${project.slug}`}>View</Link>
                        <Link className="card-link" to={`/admin/projects/${project.project_id}/edit`}>Edit</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Blog Posts Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Blog Posts</h2>
              <Link to="/admin/posts/new" className="action-button">+ Create Post</Link>
            </div>
            {posts.length === 0 ? (
              <div className="empty-state">No blog posts yet. Write your first post!</div>
            ) : (
              <div className="grid gap-12">
                {posts.map(post => (
                  <div key={post.post_id} className="dashboard-card">
                    <div className="card-content">
                      <div>
                        <div className="card-title">{post.title}</div>
                        {post.user && (
                          <div className="card-meta">
                            By {post.user.firstName} {post.user.lastName} • @{post.user.username}
                          </div>
                        )}
                      </div>
                      <div className="card-actions">
                        <Link className="card-link" to={`/blog/${post.slug}`}>View</Link>
                        <Link className="card-link" to={`/admin/posts/${post.post_id}/edit`}>Edit</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default Dashboard